import React, { ReactElement, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { generateSpecialPassword } from "helpers/generic";

import { isNil } from "ramda";

import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { mailboxContainerSteps } from "constants/apps";

import IconSelector from "components/shared/images/IconSelector";
import AppModal from "components/shared/modal/AppModal";
import RangeSlider from "components/shared/sliders/RangeSlider";

import { postCreateContainerMailbox } from "redux/handlers/mailDomainContainerHandler";

import { getContainerMailboxes } from "redux/selectors/containerMailSelector";

import { useAppDispatch } from "hooks/reduxHook";

import { mailboxData } from "types/global/container";

import MailboxRecap from "./mailboxStepComponents/MailboxRecap";

const initialValue = {
 name: "",
 password: "",
 quota: 0,
 imap: false,
 pop: false,
 smtp: false,
 sieve: false,
 threshold1: 8,
 threshold2: 12
};

const CreateMailbox = ({
 id,
 server_id,
 domain,
 available_quota
}: {
 id: number;
 server_id: number;
 domain: string;
 available_quota: number;
}): ReactElement => {
 const intl = useIntl();
 const dispatch = useAppDispatch();

 const mailboxes = useSelector(getContainerMailboxes);

 const [open, setOpen] = useState<boolean>(false);
 const [loading, setLoading] = useState<boolean>(false);
 const [goToStepper, setGoToStepper] = useState<boolean>(false);
 const [activeStep, setActiveStep] = useState<number>(0);
 const [mailboxData, setMailboxData] = useState<mailboxData>(initialValue);
 const [showPassword, setShowPassword] = useState<string>("password");

 const handleGeneratePassword = () => {
  handleChange("password", generateSpecialPassword());
 };

 const handleOpen = async () => {
  handleReset();
  setOpen(true);
 };
 const handleClose = () => setOpen(false);

 const handleConfirm = async () => {
  setLoading(true);
  await dispatch(
   postCreateContainerMailbox(
    server_id,
    id,
    mailboxData.name,
    mailboxData.password,
    mailboxData.quota,
    mailboxData.imap,
    mailboxData.pop,
    mailboxData.smtp,
    mailboxData.sieve,
    `${mailboxData.threshold1}.${mailboxData.threshold2}`
   )
  );
  setLoading(false);
  handleClose();
 };

 const handleNext = () => {
  setActiveStep((prevActiveStep) => prevActiveStep + 1);
 };

 const handleBack = () => {
  setActiveStep((prevActiveStep) => prevActiveStep - 1);
 };

 const handleReset = () => {
  setMailboxData(initialValue);
  setActiveStep(0);
 };

 const handleChange = (key: keyof mailboxData, value: string | boolean | number) => {
  setMailboxData({ ...mailboxData, [key]: value });
 };

 const handleChangeSpam = (value: Array<number>) => {
  setMailboxData({
   ...mailboxData,
   threshold1: value[0],
   threshold2: value[1]
  });
 };

 const checkName = (): boolean => {
  return (
   mailboxData.name !== "" &&
   !isNil(
    mailboxes?.dataset.find((element) => element.username === `${mailboxData.name}@${domain}`)
   )
  );
 };

 const RenderStepContent = (): ReactElement => {
  switch (activeStep) {
   case 0:
    return (
     <Stack spacing={2}>
      <TextField
       autoComplete="off"
       value={mailboxData.name}
       autoFocus
       label={intl.formatMessage({ id: "container.maildomains.mailboxName" })}
       InputLabelProps={{ shrink: true }}
       helperText={<FormattedMessage id="container.maildomains.mailboxNameHelp" />}
       onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();
       }}
       onChange={({ currentTarget }) => handleChange("name", currentTarget.value)}
       InputProps={{
        endAdornment: <InputAdornment position="end">{`@${domain}`}</InputAdornment>
       }}
      />
      {checkName() && (
       <Typography fontStyle="italic" variant="caption" color="error">
        <FormattedMessage id="container.maildomains.mailboxExists" />
       </Typography>
      )}
     </Stack>
    );
   case 1:
    return (
     <Stack direction="row" alignItems="center" spacing={2}>
      <TextField
       value={mailboxData.password}
       onChange={({ currentTarget }) => handleChange("password", currentTarget.value)}
       fullWidth={true}
       autoFocus
       type={showPassword}
       autoComplete="new-password"
       label={intl.formatMessage({ id: "container.maildomains.mailboxPassword" })}
       error={mailboxData.password.length < 8}
       InputProps={{
        endAdornment: (
         <InputAdornment position="end">
          <IconButton
           aria-label="toggle password visibility"
           onClick={() => setShowPassword(showPassword === "password" ? "text" : "password")}
           edge="end">
           <IconSelector
            icon={showPassword === "text" ? "VisibilityOffIcon" : "VisibilityIcon"}
            props={{ fontSize: "small" }}
           />
          </IconButton>
         </InputAdornment>
        )
       }}
      />
      <Button onClick={handleGeneratePassword} sx={{ width: "40%" }}>
       <FormattedMessage id="app.generatePassword" />
      </Button>
     </Stack>
    );
   case 2:
    return (
     <Stack p={2} spacing={2}>
      <FormControlLabel
       control={
        <Checkbox
         checked={mailboxData.imap}
         onChange={(e) => {
          handleChange("imap", e.target.checked);
         }}
        />
       }
       label={intl.formatMessage({ id: "container.maildomains.enableImap" })}
      />
      <FormControlLabel
       control={
        <Checkbox
         checked={mailboxData.pop}
         onChange={(e) => {
          handleChange("pop", e.target.checked);
         }}
        />
       }
       label={intl.formatMessage({ id: "container.maildomains.enablePop" })}
      />
      <FormControlLabel
       control={
        <Checkbox
         checked={mailboxData.smtp}
         onChange={(e) => {
          handleChange("smtp", e.target.checked);
         }}
        />
       }
       label={intl.formatMessage({ id: "container.maildomains.enableSmtp" })}
      />
      <FormControlLabel
       control={
        <Checkbox
         checked={mailboxData.sieve}
         onChange={(e) => {
          handleChange("sieve", e.target.checked);
         }}
        />
       }
       label={intl.formatMessage({ id: "container.maildomains.enableSieve" })}
      />
      <TextField
       fullWidth={true}
       type="number"
       label={intl.formatMessage({ id: "container.maildomains.mailboxQuota" })}
       error={mailboxData.quota < 0 || isNil(mailboxData.quota)}
       InputLabelProps={{ shrink: true }}
       sx={{ my: 2 }}
       autoFocus
       value={mailboxData.quota}
       onChange={(e) => handleChange("quota", Number(e.target.value))}
       autoComplete="off"
       helperText={`${
        mailboxData.quota < 0 || isNil(mailboxData.quota)
         ? intl.formatMessage({ id: "container.maildomains.mailboxQuotaError" })
         : intl.formatMessage({ id: "container.maildomains.mailboxQuotaExplain" })
       }\nMax available quota: ${available_quota}`}
      />
     </Stack>
    );
   case 3:
    return (
     <Stack>
      <Stack direction="row" spacing={1}>
       <Typography variant="subtitle2" fontWeight="bold">
        <FormattedMessage id="container.maildomains.threshold" />
        {":"}
       </Typography>
       <Typography
        variant="kxColored"
        fontWeight="bold">{`${mailboxData.threshold1} - ${mailboxData.threshold2}`}</Typography>
      </Stack>
      <RangeSlider
       max={15}
       min={0}
       defaultValue={[mailboxData.threshold1, mailboxData.threshold2]}
       changeValue={(value) => handleChangeSpam(value)}
      />
      <Typography variant="extraSmall">
       <FormattedMessage id="container.maildomains.thresholdHelp" />
      </Typography>
     </Stack>
    );
   case 4:
    return <MailboxRecap mailboxData={mailboxData} />;
   default:
    return <></>;
  }
 };

 const renderThirdText = (): string => {
  if (!goToStepper) {
   return "docker.website.startNow";
  } else if (activeStep === 4) {
   return "app.confirm";
  } else {
   return "app.next";
  }
 };

 const handleConfirmThird = () => {
  if (!goToStepper) {
   setGoToStepper(true);
  } else if (activeStep === 4) {
   handleConfirm();
  } else {
   handleNext();
  }
 };

 const checkDisableThird = (): boolean => {
  return (
   goToStepper &&
   (loading ||
    (activeStep === 0 && mailboxData.name === "") ||
    (activeStep === 1 && mailboxData.password === "") ||
    (activeStep === 2 && mailboxData.quota > available_quota))
  );
 };

 return (
  <>
   <Button onClick={handleOpen}>
    <FormattedMessage id="container.maildomains.createMailbox" />
   </Button>
   <AppModal
    open={open}
    close={handleClose}
    title={intl.formatMessage({ id: "container.maildomains.createMailbox" })}
    handleClose={() => (goToStepper ? handleBack() : handleClose())}
    handleConfirm={handleReset}
    thirdOption={true}
    thirdText={renderThirdText()}
    disabled={loading}
    handleConfirmThird={handleConfirmThird}
    confirmText={"app.reset"}
    disableCancel={activeStep === 0 && goToStepper}
    showConfirmButton={goToStepper}
    disabledThird={loading || checkDisableThird()}>
    {!goToStepper ? (
     <Stack>
      <Stack direction="row">
       <Stack justifyContent="center" spacing={2}>
        <Typography>
         <FormattedMessage id="container.maildomains.firstExplainMailbox" />
        </Typography>
        <Typography>
         <FormattedMessage id="container.maildomains.secondExplainMailbox" />
        </Typography>
        <Typography>
         <FormattedMessage id="container.maildomains.thirdExplainMailbox" />
        </Typography>
       </Stack>
       <Stack alignItems="center" justifyContent="center">
        <IconSelector icon="InboxIcon" props={{ sx: { fontSize: "200px", color: "lightgray" } }} />
       </Stack>
      </Stack>
     </Stack>
    ) : (
     <Stack>
      <Stepper activeStep={activeStep} alternativeLabel>
       {mailboxContainerSteps.map((label) => (
        <Step key={label.label}>
         <StepLabel optional={label.description ? <FormattedMessage id={label.description} /> : ""}>
          {<FormattedMessage id={label.label} />}
         </StepLabel>
        </Step>
       ))}
      </Stepper>
      <Stack p={2}>
       <RenderStepContent />
      </Stack>
     </Stack>
    )}
   </AppModal>
  </>
 );
};

export default CreateMailbox;
